import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { X, ChevronDown, ChevronUp } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/authContext";
import { supabase } from "@/lib/supabase";

export function CookieBanner() {
    const [isVisible, setIsVisible] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);
    const { t } = useI18n();
    const { user } = useAuth();

    const [prefs, setPrefs] = useState({
        functional: true, // Always true
        analytical: true,
        marketing: false,
    });

    useEffect(() => {
        const saved = localStorage.getItem("vintmap_cookie_prefs");
        if (!saved) {
            // Show with a small delay for better UX
            const timer = setTimeout(() => setIsVisible(true), 1000);
            return () => clearTimeout(timer);
        } else {
            try {
                const parsed = JSON.parse(saved);
                setPrefs(prev => ({ ...prev, ...parsed, functional: true }));
            } catch (e) {
                localStorage.removeItem("vintmap_cookie_prefs");
                setIsVisible(true);
            }
        }
    }, []);

    const saveToBackend = async (newPrefs: typeof prefs) => {
        if (!user) return;
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) return;

            await fetch("/api/profiles/me", {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${session.access_token}`
                },
                body: JSON.stringify({
                    consent_analytical: newPrefs.analytical,
                    consent_marketing: newPrefs.marketing,
                    consent_ads: newPrefs.marketing // Map marketing to ads for now
                })
            });
        } catch (e) {
            console.error("[CookieBanner] Sync failed", e);
        }
    };

    const handleAcceptAll = () => {
        const allIn = { functional: true, analytical: true, marketing: true };
        localStorage.setItem("vintmap_cookie_prefs", JSON.stringify(allIn));
        setPrefs(allIn);
        setIsVisible(false);
        saveToBackend(allIn);
    };

    const handleSavePreferences = () => {
        localStorage.setItem("vintmap_cookie_prefs", JSON.stringify(prefs));
        setIsVisible(false);
        saveToBackend(prefs);
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 md:bottom-5 md:right-5 md:left-auto bg-background/98 backdrop-blur-md border-t md:border border-border p-3.5 md:p-4 shadow-2xl z-[99999] animate-in slide-in-from-bottom duration-500 md:max-w-[320px] md:rounded-xl">
            <div className="flex flex-col gap-2.5">
                <div className="flex items-start justify-between gap-2.5">
                    <div className="text-sm text-foreground flex-1">
                        <div className="flex items-center gap-1.5 mb-1">
                            <span className="text-lg">🍪</span>
                            <h3 className="font-bold text-sm tracking-tight">{t("cookie.title")}</h3>
                        </div>
                        <p className="text-[11px] text-muted-foreground leading-snug">
                            {t("cookie.desc")}{" "}
                            <Link href="/privacy" className="text-primary font-medium underline hover:text-primary/80">{t("footer.privacy")}</Link>
                        </p>
                    </div>
                    <Button
                        onClick={() => setIsVisible(false)}
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-foreground shrink-0 h-5 w-5 -mt-0.5"
                    >
                        <X className="h-3.5 w-3.5" />
                    </Button>
                </div>

                {showPreferences && (
                    <div className="flex flex-col gap-2 py-2.5 border-y border-border/50 animate-in fade-in slide-in-from-top-1">
                        <div className="flex items-center justify-between p-1.5 rounded-lg border border-border bg-muted/20">
                            <div className="flex flex-col gap-0">
                                <Label className="text-[11px] font-semibold">{t("cookie.functional")}</Label>
                                <p className="text-[9px] text-muted-foreground leading-tight">{t("cookie.functional.desc")}</p>
                            </div>
                            <Switch checked disabled className="scale-[0.65] origin-right" />
                        </div>

                        <div className="flex items-center justify-between p-1.5 rounded-lg border border-border bg-muted/20">
                            <div className="flex flex-col gap-0">
                                <Label htmlFor="pref-analytical" className="text-[11px] font-semibold">{t("cookie.analytical")}</Label>
                                <p className="text-[9px] text-muted-foreground leading-tight">{t("cookie.analytical.desc")}</p>
                            </div>
                            <Switch
                                id="pref-analytical"
                                checked={prefs.analytical}
                                onCheckedChange={(v) => setPrefs(p => ({ ...p, analytical: v }))}
                                className="scale-[0.65] origin-right"
                            />
                        </div>

                        <div className="flex items-center justify-between p-1.5 rounded-lg border border-border bg-muted/20">
                            <div className="flex flex-col gap-0">
                                <Label htmlFor="pref-marketing" className="text-[11px] font-semibold">{t("cookie.marketing")}</Label>
                                <p className="text-[9px] text-muted-foreground leading-tight">{t("cookie.marketing.desc")}</p>
                            </div>
                            <Switch
                                id="pref-marketing"
                                checked={prefs.marketing}
                                onCheckedChange={(v) => setPrefs(p => ({ ...p, marketing: v }))}
                                className="scale-[0.65] origin-right"
                            />
                        </div>
                    </div>
                )}

                <div className="flex items-center gap-2">
                    <Button onClick={handleAcceptAll} className="flex-1 h-8 text-[11px] font-semibold">
                        {t("cookie.accept")}
                    </Button>
                    <Button
                        onClick={showPreferences ? handleSavePreferences : () => setShowPreferences(true)}
                        variant="outline"
                        className="flex-1 h-8 text-[11px] font-medium"
                    >
                        {showPreferences ? t("cookie.save") : t("cookie.preferences")}
                    </Button>
                </div>
            </div>
        </div>
    );
}
